import type { AuditEvent } from "@/api/client";
import {
  BATCHES_PERMISSIONS,
  CERTIFICATES_PERMISSIONS,
  MEMBERS_PERMISSIONS,
  PRINTERS_PERMISSIONS,
  PROGRAMS_PERMISSIONS,
  ROLES_PERMISSIONS,
  SIGNATURES_PERMISSIONS,
  TEMPLATES_PERMISSIONS,
  USERS_PERMISSIONS,
} from "@/app/nav";

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const ENTITY_TYPES = [
  "batch",
  "certificate",
  "font",
  "member",
  "payment",
  "print_spool",
  "printer_profile",
  "program",
  "role",
  "signature",
  "template",
  "template_asset",
  "user",
] as const;

/** RFC 3339 to a datetime-local input's value, in local time. */
export function toLocalInput(iso: string | undefined) {
  if (!iso) return "";
  const when = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${when.getFullYear()}-${pad(when.getMonth() + 1)}-${pad(when.getDate())}T${pad(when.getHours())}:${pad(when.getMinutes())}`;
}

/** A datetime-local value, read as local time, to RFC 3339; undefined when empty. */
export function fromLocalInput(value: string) {
  if (value === "") return undefined;
  const ms = new Date(value).getTime();
  return Number.isNaN(ms) ? undefined : new Date(ms).toISOString();
}

export function utcLabel(iso: string) {
  return `${new Date(iso).toISOString().slice(0, 19).replace("T", " ")} UTC`;
}

export function actorLabel(event: AuditEvent) {
  return event.actor_name ?? event.actor_label ?? event.actor_user_id ?? "-";
}

type EntityLink =
  | { to: "/users/$userId"; params: { userId: string } }
  | { to: "/roles/$roleId"; params: { roleId: string } }
  | { to: "/programs/$programId"; params: { programId: string } }
  | { to: "/templates/$templateId"; params: { templateId: string } }
  | { to: "/signatures/$signatureId"; params: { signatureId: string } }
  | { to: "/batches/$batchId"; params: { batchId: string } }
  | { to: "/certificates/$certificateId"; params: { certificateId: string } }
  | { to: "/members/$memberId"; params: { memberId: string } }
  | { to: "/printers/$printerId"; params: { printerId: string } };

const allowed = (can: (permission: string) => boolean, permissions: readonly string[]) => permissions.every(can);

/** The entity's own page, when it has one, the id is a record id (a failed login's is an email), and the viewer may open it. */
export function entityLink(event: AuditEvent, can: (permission: string) => boolean): EntityLink | null {
  const id = event.entity_id;
  if (!UUID.test(id)) return null;
  switch (event.entity_type) {
    case "user":
      return allowed(can, USERS_PERMISSIONS) ? { to: "/users/$userId", params: { userId: id } } : null;
    case "role":
      return allowed(can, ROLES_PERMISSIONS) ? { to: "/roles/$roleId", params: { roleId: id } } : null;
    case "program":
      return allowed(can, PROGRAMS_PERMISSIONS) ? { to: "/programs/$programId", params: { programId: id } } : null;
    case "template":
      return allowed(can, TEMPLATES_PERMISSIONS) ? { to: "/templates/$templateId", params: { templateId: id } } : null;
    case "signature":
      return allowed(can, SIGNATURES_PERMISSIONS) ? { to: "/signatures/$signatureId", params: { signatureId: id } } : null;
    case "batch":
      return allowed(can, BATCHES_PERMISSIONS) ? { to: "/batches/$batchId", params: { batchId: id } } : null;
    case "certificate":
      return allowed(can, CERTIFICATES_PERMISSIONS) ? { to: "/certificates/$certificateId", params: { certificateId: id } } : null;
    case "member":
      return allowed(can, MEMBERS_PERMISSIONS) ? { to: "/members/$memberId", params: { memberId: id } } : null;
    case "printer_profile":
      return allowed(can, PRINTERS_PERMISSIONS) ? { to: "/printers/$printerId", params: { printerId: id } } : null;
    default:
      return null;
  }
}

// Types the API resolves to a name; a null label on one of these means the record is gone.
const LABELLED = new Set(["batch", "certificate", "member", "user", "program", "template", "email_template", "printer_profile", "payment"]);

export function entityTypeLabel(type: string) {
  const words = type.replaceAll("_", " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** "Batch: Induction A", "Batch (deleted)", or just "Role" where the API has no name for the type. */
export function entityText(event: Pick<AuditEvent, "entity_type" | "entity_id" | "entity_label">) {
  const type = entityTypeLabel(event.entity_type);
  if (event.entity_label) return `${type}: ${event.entity_label}`;
  // Not a record id (a failed login's is an email), so it already reads well.
  if (!UUID.test(event.entity_id)) return `${type}: ${event.entity_id}`;
  return LABELLED.has(event.entity_type) ? `${type} (deleted)` : type;
}
